import { Logger } from '@icommerce/logger';
import type { Header } from '@hub/layout/Header/types';
import { LocaleContext, getLocaleForContext } from '@icommerce/i18n';

/**
 * Fetches detailed header data for client-side hydration.
 */
export const fetchClientHeaderData = async (locale: string): Promise<Header | null> => {
  const localeSlug = getLocaleForContext(locale, LocaleContext.Storyblok);
  const requestUrl = `/_hub/api/navigation?locale=${localeSlug.toLowerCase()}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000); // 5 seconds

  try {
    const res = await fetch(requestUrl, { method: 'GET', signal: controller.signal });

    if (!res.ok) {
      throw new Error(`Failed to fetch client header from ${requestUrl} (status: ${res.status})`);
    }

    return (await res.json()) as Header;
  } catch (error) {
    const isTimeout = (error as Error).name === 'AbortError';

    Logger.error(
      {
        message: isTimeout ? 'Fetch timed out' : 'Error fetching client header',
        error: (error as Error).message,
        locale,
      },
      'ErrorFetchingClientHeader'
    );
    return null;
  } finally {
    clearTimeout(timeout);
  }
};
